import React, { useEffect, useMemo } from 'react'
import { MainTabsV2, PlanType } from '@labrav/react-components'
import { FormikProps, useFormikContext } from 'formik'
import { scrollspyTabs } from '@labrav/common'
import { useTheme } from '@mui/material'

import { useStyles } from './FormSections.styles'
import { OfferDetails } from './OfferDetails/OfferDetails'
import { CombinedFormState } from '../../../modules/combinedForm/reducer'
import { CombinedFormPaymentConfiguration } from './PaymentConfiguration/PaymentConfiguration'
import { EulaSectionCombinedForm } from './EulaSectionCombinedForm/EulaSectionCombinedForm'
import { ContractDurationCombinedForm } from './ContractDurationCombinedForm/ContractDurationCombinedForm'
import { RenewalSectionCombinedForm } from './RenewalSectionCombinedForm/RenewalSectionCombinedForm'
import { PlansCombinedForm } from './PlansCombinedForm/PlansCombinedForm'
import { UsagePlans } from './UsagePlans/UsagePlans'
import { PricingSaasSubscriptions } from './PricingSaasSubscriptions/PricingSaasSubscriptions'
import CombinedPricing from './Pricing/CombinedPricing'
import { FlyOutListingData } from '../../../types/flyout'
import { useSelector } from '../../../store'
import { Product } from '../../../modules/productsListing/reducer'
import {
  getUpdatedDimensionsForEmptyPOAndResaleContract,
  getUpdatedDimensionsForEmptyPOAndResaleSubscription,
} from '../../../modules/privateOffer/privateOfferCreation/utils/dimensionHelper'

export type FormSectionsProps = {
  offset: number
  tabTop?: string
  productType?: PlanType | undefined
  disabled: boolean
  labraFormId?: string
  eulaUrl: string
  flyoutSellerInfo: FlyOutListingData
}

export function FormSections({
  offset,
  tabTop,
  productType,
  disabled,
  labraFormId,
  eulaUrl,
  flyoutSellerInfo,
}: FormSectionsProps) {
  const theme = useTheme()
  const { classes } = useStyles({ offset })
  const { values, setFieldValue } =
    useFormikContext() as FormikProps<CombinedFormState>
  const productIdToUse = values.resellerAgreementData.awsProductId
  const products = useSelector<Product[]>(
    state => state.productsListing['AWS']?.products?.rows || []
  )
  const product =
    products.find(product => product.productId === productIdToUse) ||
    ({} as Product)
  const productDimensions = useMemo(() => {
    return (
      product?.productDimensions?.filter(
        dimension => dimension.isAdditionalUsageAllowed === true
      ) || []
    )
  }, [product])

  const constructDimensionObject =
    getUpdatedDimensionsForEmptyPOAndResaleContract(
      productDimensions,
      false,
      true
    )
  const constructSubscriptionDimensionObect =
    getUpdatedDimensionsForEmptyPOAndResaleSubscription(
      product.productDimensions || [],
      false,
      true
    )
  const updatedDimesion =
    productType === PlanType.SAAS_SUBSCRIPTION
      ? constructSubscriptionDimensionObect
      : constructDimensionObject

  useEffect(() => {
    const updatedDimensionsArray = Array.isArray(updatedDimesion)
      ? updatedDimesion
      : []
    const existingDimensions = Array.isArray(values.cppoData?.dimensions)
      ? values.cppoData.dimensions
      : []
    const mergedMap = new Map()

    for (const dim of updatedDimensionsArray) {
      mergedMap.set(dim.dimensionId, dim)
    }
    for (const dim of existingDimensions) {
      mergedMap.set(dim.dimensionId, dim)
    }
    const merged = Array.from(mergedMap.values())
    const adjusted = merged.map(dim => {
      if (dim.prices) {
        const adjustmentPercentage = values.cppoData?.adjustmentPercentage ?? 0
        const adjustmentFactor = 1 + Number(adjustmentPercentage ?? 0) / 100

        if (productType === PlanType.SAAS_SUBSCRIPTION) {
          dim.prices.subscriptionPrice =
            dim.prices.subscriptionPrice !== null
              ? dim.prices.subscriptionPrice / adjustmentFactor
              : null
        } else {
          dim.prices.additionalUsagePrice =
            dim.prices.additionalUsagePrice !== null
              ? dim.prices.additionalUsagePrice / adjustmentFactor
              : null
        }
      }
      return dim
    })

    const updatedArray =
      productType === PlanType.SAAS_SUBSCRIPTION ? merged : adjusted

    setFieldValue('resellerAgreementData.dimensions', updatedArray)
  }, [productIdToUse, productType])

  const filterdDimensions = values.resellerAgreementData.dimensions.filter(
    dimension =>
      productType !== PlanType.SAAS_SUBSCRIPTION
        ? dimension.type === 'additional_usage'
        : true
  )
  const ContainerOfferTabs = [
    {
      key: 'offer-details',
      label: 'Offer details',
      renderElement: (
        <div id={'offer-details'} className={classes.scrollTopMargin}>
          <OfferDetails productType={productType} disabled={disabled} />
          <CombinedFormPaymentConfiguration
            disabled={disabled}
            flyoutSellerInfo={flyoutSellerInfo}
          />

          {productType !== PlanType.SAAS_SUBSCRIPTION && (
            <ContractDurationCombinedForm disabled={disabled} />
          )}
        </div>
      ),
    },
    {
      key: 'offer-pricing',
      label: 'Pricing',
      renderElement: (
        <div id={'offer-pricing'} className={classes.scrollTopMargin}>
          {productType !== PlanType.SAAS_SUBSCRIPTION && (
            <PlansCombinedForm
              addButtonName={'Choose plans'}
              disabled={disabled}
            />
          )}
          {productType !== PlanType.SAAS_SUBSCRIPTION && (
            <CombinedPricing disabled={disabled} />
          )}

          {filterdDimensions.length > 0 && (
            <UsagePlans
              productType={productType}
              disabled={disabled}
              labraFormId={labraFormId}
            />
          )}
          {productType === PlanType.SAAS_SUBSCRIPTION && !disabled && (
            <PricingSaasSubscriptions disabled={disabled} />
          )}
        </div>
      ),
    },
    {
      key: 'offer-other',
      label: 'Other',
      renderElement: (
        <div id={'offer-other'} className={classes.scrollTopMargin}>
          <RenewalSectionCombinedForm disabled={disabled} />
          <EulaSectionCombinedForm
            receivedResaleAuthEulaUrl={eulaUrl}
            disabled={disabled}
          />
        </div>
      ),
    },
  ]
  const { activeTabInex, scrollToSection } = scrollspyTabs({
    tabs: ContainerOfferTabs,
    offset: offset,
  })

  return (
    <div className={classes.root}>
      <MainTabsV2
        activeTabIndex={activeTabInex}
        onChange={scrollToSection}
        tabs={ContainerOfferTabs}
        backGroundColor={theme.palette.textBackground.light}
        tabPosition="sticky"
        tabTop={tabTop}
      />
      {ContainerOfferTabs.map(
        containerOfferTabItem => containerOfferTabItem.renderElement
      )}
    </div>
  )
}
